import { formatDate as formatDateFromDateUtils } from '../dateUtils';
import {
  calculateAge,
  filestackPath,
  formatPhoneNumber,
  ia,
  kebabToReadable,
  safeParseJSON
} from '../utility';

import { GAP } from './lib/constants';
import { drawHorizontalLine } from './lib/helpers';

const marginX = GAP / 2;
const lineSpacing = 5;
const smallSpacing = 2;

export const getImageFileId = (image) => {
  const practiceImage = safeParseJSON(image, image);
  const finalImageId =
    typeof practiceImage === 'string' && !practiceImage.includes('{')
      ? practiceImage
      : practiceImage?.jpg || practiceImage?.png;

  return finalImageId;
};

const generateHeaderImage = (doc, image) => {
  const finalImageId = getImageFileId(image);

  if (!finalImageId) {
    return;
  }

  const img = filestackPath(finalImageId);

  const maxWidth = 60;
  const maxHeight = 30;

  //This fixes image to contain the max width and height
  try {
    const { width: imgWidth, height: imgHeight } = doc.getImageProperties(img);

    const widthRatio = maxWidth / imgWidth;
    const heightRatio = maxHeight / imgHeight;
    const scale = Math.min(widthRatio, heightRatio);

    const finalWidth = imgWidth * scale;
    const finalHeight = imgHeight * scale;

    //This centers the image inside the box
    const yPos = GAP + (maxHeight - finalHeight) / 2;

    //This adds the image to the document
    doc.addImage(img, 'JPEG', marginX, yPos, finalWidth, finalHeight);
  } catch (error) {
    console.error('Error loading image:', error);
  }
};

export const generateHeader = ({
  doc,
  startY = GAP,
  headerConfigs,
  formatDate = formatDateFromDateUtils
}) => {
  doc.setFontSize(10);

  let finalStartY = startY;

  const { practice, patient, payer, rightSidePatientInfo, hiddenFields } = headerConfigs || {};

  const pageWidth = doc.internal.pageSize.width;

  if (practice) {
    const practiceDetails = [
      { text: practice.tax_id ? `Tax ID: ${practice.tax_id}` : '', fontSize: 12, weight: 'bold' },
      { text: practice.displayName || '', weight: 'bold' },
      practice.fax || '',
      getAddressDisplay(practice?.address) || '',
      formatPhoneNumber(practice.phone) || '',
      practice.email || '',
      practice?.website || ''
    ].filter(Boolean);

    let practiceTextY = 15;
    const practiceTextX = pageWidth - marginX;

    const practiceSectionHeight = practiceDetails.length * lineSpacing + practiceTextY;

    doc.setFillColor('#FAFAFA');
    doc.rect(0, 0, pageWidth, practiceSectionHeight, 'F');
    drawHorizontalLine(doc, practiceSectionHeight, { color: '#F5F5F5', lineWidth: 0.1 });

    practiceDetails.forEach((el) => {
      const text = typeof el === 'string' ? el : typeof el?.text === 'string' ? el.text : '';
      const weight = el?.weight || 'normal';
      const fontSize = el?.fontSize || 10;

      if (el?.fontSize || el?.weight) {
        doc.setFont('helvetica', weight);
        doc.setFontSize(fontSize);
      }

      const textWidth = doc.getTextWidth(text);
      doc.text(text, practiceTextX - textWidth, practiceTextY);
      practiceTextY += lineSpacing;

      if (el?.fontSize || el?.weight) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
      }
    });

    finalStartY += practiceDetails.length * lineSpacing + lineSpacing / 2;
  }

  if (practice?.header_photo) {
    generateHeaderImage(doc, practice.header_photo);
  }

  doc.setFillColor('#FFFFFF');

  finalStartY += GAP;
  let rightSidePatientInfoY = finalStartY;

  if (patient) {
    const hideName = hiddenFields?.patient?.includes('pt_name');
    const hidePtDob = hiddenFields?.patient?.includes('pt_dob');
    const hidePtAge = hiddenFields?.patient?.includes('pt_age');
    const hidePtGender = hiddenFields?.patient?.includes('pt_gender');
    const hidePtAddress = hiddenFields?.patient?.includes('pt_address');

    // LEFT SIDE
    if (!hideName && !!patient?.fullName) {
      doc.setFont('helvetica', 'bold');
      doc.text(patient.fullName, marginX, finalStartY);
      doc.setFont('helvetica', 'normal');

      finalStartY += lineSpacing;
    }

    // Patient details
    const detailsParts = [];
    if (!!patient?.id) {
      detailsParts.push(`#${patient.id}`);
    }
    if (patient?.dob) {
      if (!hidePtDob) {
        let dateOfBirth = formatDateFromDateUtils(patient?.dob, practice?.timezone);
        if (!hidePtAge) {
          dateOfBirth += ` (${calculateAge({ dateOfBirth: patient.dob })}y)`;
        }
        detailsParts.push(dateOfBirth);
      }
    }
    if (!hidePtGender && patient?.gender) {
      detailsParts.push(kebabToReadable(patient.gender));
    }
    if (detailsParts.length) {
      doc.text(detailsParts.join(' • '), marginX, finalStartY);
      finalStartY += lineSpacing;
    }
    // END Patient details

    if (!hidePtAddress && !!patient?.userAddress) {
      doc.text(getAddressDisplay(patient.userAddress), marginX, finalStartY);
      finalStartY += lineSpacing;
    }

    // RIGHT SIDE Patient Info
    const rightStartX = pageWidth - marginX;

    if (ia(rightSidePatientInfo)) {
      rightSidePatientInfo.forEach(({ title, value }, index) => {
        doc.setFontSize(9);
        doc.setTextColor('#717680');
        doc.text(title, rightStartX - doc.getTextWidth(title), rightSidePatientInfoY);
        doc.setFontSize(10);
        doc.setTextColor('black');

        rightSidePatientInfoY += lineSpacing;

        doc.text(value, rightStartX - doc.getTextWidth(value), rightSidePatientInfoY);

        rightSidePatientInfoY += lineSpacing;
        // add some spacing between the right side patient info items if it's not the last item
        if (index !== rightSidePatientInfo.length - 1) {
          rightSidePatientInfoY += smallSpacing;
        }
      });
    }

    if (!!patient.service_date) {
      const dateServiceText = `Date of Service: ${formatDate(patient?.service_date, practice?.timezone)}`;
      doc.text(dateServiceText, rightStartX - doc.getTextWidth(dateServiceText), finalStartY + 10);
    }
  }

  if (payer) {
    finalStartY += smallSpacing; // add some spacing between the patient details and the payer details

    if (payer.payer_name && payer.payerid) {
      doc.setFont('helvetica', 'bold');
      doc.text(`${payer.payer_name} (${payer.payerid})`, marginX, finalStartY);
      doc.setFont('helvetica', 'normal');
      finalStartY += lineSpacing;
    }

    if (payer.payer_city && payer.payer_addr_1 && payer.payer_zip) {
      doc.text(
        `${payer.payer_city} ${payer.payer_addr_1}, ${payer.payer_zip}`,
        marginX,
        finalStartY
      );
      finalStartY += lineSpacing;
    }
  }

  return Math.max(finalStartY, rightSidePatientInfoY);
};

function getAddressDisplay(address) {
  if (address && typeof address === 'object' && !ia(address)) {
    return address?.fullAddress;
  }

  if (address && typeof address !== 'number') {
    return address;
  }

  return null;
}
